import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { roundsAPI, coursesAPI, fridaveAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { ROUTES } from '../../utils/constants';
import LoadingSpinner from '../../components/common/LoadingSpinner';

export default function FriDaveCreateEvent() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [nextEventNumber, setNextEventNumber] = useState(1);

  const currentYear = String(new Date().getFullYear());
  const [form, setForm] = useState({
    courseId: '',
    date: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [courseData, roundData] = await Promise.all([
        coursesAPI.getAll(),
        fridaveAPI.getRounds(currentYear).catch(() => []),
      ]);
      const list = Array.isArray(courseData) ? courseData : (courseData?.courses || []);
      setCourses(list);
      const bath = list.find(c => c.name?.toLowerCase().includes('bath'));
      if (bath) setForm(f => ({ ...f, courseId: String(bath.id) }));
      const rounds = Array.isArray(roundData) ? roundData : [];
      setNextEventNumber(rounds.length + 1);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.courseId) { setError('Please select a course'); return; }

    setSubmitting(true);
    setError('');

    try {
      await roundsAPI.createEmptyRound({
        eventName: `FriDave Event ${nextEventNumber}`,
        eventType: 'fridave',
        seriesSeason: currentYear,
        courseId: parseInt(form.courseId),
        roundType: 'championship',
        startDate: form.date || new Date().toISOString(),
      });
      navigate(ROUTES.FRIDAVE_ROUNDS);
    } catch (err) {
      const errData = err.response?.data?.error;
      setError(typeof errData === 'string' ? errData : err.response?.data?.message || 'Failed to create event');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><LoadingSpinner /></div>;

  if (!isAuthenticated) {
    return (
      <div className="px-4 pb-4">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate(ROUTES.FRIDAVE_ROUNDS)} className="text-fd-primary font-semibold flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h1 className="text-xl font-bold text-fd-primary">Create Event</h1>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center">
          <div className="text-5xl mb-4">🔒</div>
          <p className="text-gray-600 font-medium">Admin login required.</p>
          <p className="text-gray-400 text-sm mt-1">Use the menu to log in.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 pb-4">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(ROUTES.FRIDAVE_ROUNDS)} className="text-fd-primary font-semibold flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <h1 className="text-xl font-bold text-fd-primary">Create Event</h1>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
        <form onSubmit={handleSubmit} className="space-y-4">

          {/* Event number (auto-generated) */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Event</label>
            <div className="border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 text-sm text-gray-700 font-medium">
              Event {nextEventNumber}
            </div>
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Date</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-fd-gold"
            />
          </div>

          {/* Course */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Course</label>
            <select
              value={form.courseId}
              onChange={(e) => setForm(f => ({ ...f, courseId: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-fd-gold"
              required
            >
              <option value="">Select course...</option>
              {courses.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {error && <p className="text-red-600 text-sm font-medium">{error}</p>}

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 bg-fd-primary text-fd-gold font-bold rounded-lg hover:bg-fd-secondary transition-colors disabled:opacity-50 mt-2"
          >
            {submitting ? 'Creating...' : 'Create Event'}
          </button>
        </form>
      </div>
    </div>
  );
}
